import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PipelineVisual } from "@/components/financials/PipelineVisual";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

const fetchTractionMetrics = async () => {
  const { data, error } = await supabase
    .from("traction_metrics")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    throw error;
  }

  return data;
};

const TractionMetrics = () => {
  const {
    data: metrics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["traction-metrics"],
    queryFn: fetchTractionMetrics,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-8 w-1/2 mt-4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription></AlertDescription>
      </Alert>
    );
  }

  if (!metrics?.length) {
    return null; // Remove the "No traction metrics" message by returning null
  }

  // Group metrics by category
  const groupedMetrics = metrics.reduce((acc, metric) => {
    if (!acc[metric.category]) {
      acc[metric.category] = [];
    }
    acc[metric.category].push(metric);
    return acc;
  }, {} as Record<string, typeof metrics>);

  return (
    <div className="space-y-8">
      {Object.entries(groupedMetrics).map(([category, categoryMetrics]) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="text-lg capitalize">
              {category.replace(/_/g, " ")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryMetrics.map((metric) => (
                <div
                  key={metric.id}
                  className="p-4 rounded-lg border bg-card text-card-foreground shadow-sm"
                >
                  <h3 className="font-medium text-sm text-muted-foreground">
                    {metric.metric_name}
                  </h3>
                  <p className="mt-2 text-2xl font-semibold">{metric.value}</p>
                  {metric.description && (
                    <p className="mt-2 text-sm text-muted-foreground">
                      {metric.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const Traction = () => {
  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl text-primary">
            Current Traction & Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-6">
            Our current sales pipeline and opportunity backlog demonstrate
            strong market interest and validation for our solution.
          </p>
          <PipelineVisual />
        </CardContent>
      </Card>

      <TractionMetrics />
    </div>
  );
};

export default Traction;
